// Bazaarvoice CLS reservation: hold a placeholder height while BV loads, then
// release it once BV settles so the container hugs its actual content. -- SG 20260617

const RESERVE_SELECTOR = ".bv-reserve";
const BV_HOST_SELECTOR = "[data-bv-show]";
const SETTLE_MS = 3000;
const SETTLED_CLASS = "bv-reserve--settled";

const initializedReserves = new WeakSet<HTMLElement>();

// Resolve the BV injection target inside a reserve wrapper. -- SG 20260616
const getBvHost = (reserve: HTMLElement): HTMLElement => {
  return reserve.querySelector<HTMLElement>(BV_HOST_SELECTOR) ?? reserve;
};

// Release the reserved min-height once BV has settled. The reservation only
// exists to prevent layout shift during the pre-render window; afterwards the
// container must hug whatever BV rendered. Gating release on "host is empty"
// left a tall blank gap whenever BV rendered a short non-empty state (e.g. the
// "be the first to review/ask" prompt on products with no reviews yet), because
// the full min-height floor stayed forever. min-height:0 never shrinks real
// content, so releasing unconditionally is safe for tall widgets too, and the
// collapse happens below the fold so it causes no visible shift. -- SG 20260617
const syncReservation = (reserve: HTMLElement, settled: boolean): void => {
  if (!settled) {
    return;
  }

  reserve.classList.add(SETTLED_CLASS);
};

class BvReserveController {
  private readonly reserve: HTMLElement;
  private readonly host: HTMLElement;
  private observer: MutationObserver | null = null;
  private settleTimer: number | null = null;
  private settled = false;

  constructor(reserve: HTMLElement) {
    this.reserve = reserve;
    this.host = getBvHost(reserve);
  }

  connect(): void {
    this.observer = new MutationObserver(() => {
      this.handleHostChange();
    });

    this.observer.observe(this.host, {
      childList: true,
      subtree: true,
    });

    this.settleTimer = window.setTimeout(() => {
      this.settled = true;
      this.handleHostChange();
    }, SETTLE_MS);

    this.handleHostChange();
  }

  disconnect(): void {
    this.observer?.disconnect();
    this.observer = null;

    if (this.settleTimer !== null) {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
  }

  private handleHostChange(): void {
    syncReservation(this.reserve, this.settled);
  }
}

const controllers = new WeakMap<HTMLElement, BvReserveController>();

// Attach settle/collapse behaviour to one reserve element. -- SG 20260616
const initReserve = (reserve: HTMLElement): void => {
  if (initializedReserves.has(reserve)) {
    return;
  }

  initializedReserves.add(reserve);

  const controller = new BvReserveController(reserve);
  controllers.set(reserve, controller);
  controller.connect();
};

// Scan root for reserve wrappers (including nested PLP cards). -- SG 20260616
const initReservesIn = (root: ParentNode): void => {
  if (root instanceof HTMLElement && root.matches(RESERVE_SELECTOR)) {
    initReserve(root);
  }

  const reserves = root.querySelectorAll<HTMLElement>(RESERVE_SELECTOR);

  for (const reserve of reserves) {
    initReserve(reserve);
  }
};

let domObserver: MutationObserver | null = null;

// Watch for dynamically inserted cards (facets, recommendations). -- SG 20260616
const observeNewReserves = (): void => {
  if (domObserver) {
    return;
  }

  domObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) {
          initReservesIn(node);
        }
      }
    }
  });

  domObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

const initBazaarvoiceCls = (): void => {
  initReservesIn(document);
  observeNewReserves();
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBazaarvoiceCls);
} else {
  initBazaarvoiceCls();
}
